import { AppShellContent } from "@assetwatch/ui";
import {
  createFileRoute,
  Outlet,
  useNavigate,
  useRouterState,
} from "@tanstack/react-router";
import { useEffect } from "react";
import { useRetainedListSearch } from "../components/navigation/useRetainedListState";
import { parseNavigationOrigin } from "../lib/navigationOrigin";
import { stripOriginSearch } from "../lib/navigationOriginKeys";
import { workOrderIdSchema } from "../lib/workIds";
import {
  stripRetiredWorkOrderFilters,
  workOrdersSearchSchema,
} from "../lib/workOrdersSearch";
import { WorkOrderDetailScreen } from "../screens/WorkOrderDetailScreen";
import { WorkOrdersScreen } from "../screens/WorkOrdersScreen";

export const Route = createFileRoute("/_app/work-orders")({
  component: WorkOrdersRoute,
  validateSearch: (search: Record<string, unknown>) =>
    workOrdersSearchSchema.parse(stripRetiredWorkOrderFilters(search)),
});

function WorkOrdersRoute() {
  const search = Route.useSearch();
  const routeParams = useRouterState({
    select: (state) => state.matches.at(-1)?.params,
  });
  const pathWorkOrderId = workOrderIdSchema.safeParse(
    routeParams && "workOrderId" in routeParams
      ? routeParams.workOrderId
      : undefined,
  );
  const linkedWorkOrderId = pathWorkOrderId.success
    ? pathWorkOrderId.data
    : undefined;
  const navigate = useNavigate();
  // A direct link to one Work Order is explicit, so it never restores the
  // session's list filters; the list itself does.
  const { restoring } = useRetainedListSearch("work-orders", search, {
    restore: !linkedWorkOrderId,
  });

  useEffect(() => {
    if (search.workOrderSelectedId === undefined || linkedWorkOrderId) return;
    void navigate({
      to: "/work-orders/$workOrderId",
      params: { workOrderId: search.workOrderSelectedId },
      search: (current) => ({
        ...current,
        workOrderSelectedId: undefined,
        workOrderFullPage: undefined,
      }),
      replace: true,
    });
  }, [linkedWorkOrderId, navigate, search.workOrderSelectedId]);

  if (restoring) return <AppShellContent pending>{null}</AppShellContent>;

  if (linkedWorkOrderId && search.workOrderFullPage) {
    return (
      // Keyed so a pending upload or edit draft never carries over to another
      // Work Order when the route stays mounted.
      <WorkOrderDetailScreen
        key={linkedWorkOrderId}
        origin={parseNavigationOrigin(search)}
        originSearch={stripOriginSearch(search)}
        workOrderId={linkedWorkOrderId}
      />
    );
  }

  return (
    <>
      <WorkOrdersScreen
        linkedWorkOrderId={linkedWorkOrderId}
        routeSearch={search}
      />
      <Outlet />
    </>
  );
}
